// no default React import needed with react-jsx
import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Gavel as GavelIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { IconButton, Tooltip } from '@mui/material'
import { Waypoint } from '../types/api'
import { useLocation } from 'react-router-dom'
import { adminApi, waypointsApi, segmentsApi } from '../lib/sdkClient'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`waypoint-tabpanel-${index}`}
      aria-labelledby={`waypoint-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function WaypointsPage() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const selectedTrackId = params.get('trackId')
  const [tabValue, setTabValue] = useState(0)
  const [waypoints, setWaypoints] = useState<Waypoint[]>([])
  const [moderationQueue, setModerationQueue] = useState<Waypoint[]>([])
  const [loading, setLoading] = useState(true)
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [searchTerm, setSearchTerm] = useState('')
  const [processingFilter, setProcessingFilter] = useState('')
  const [segmentFilter, setSegmentFilter] = useState<string>('')
  const [segmentsMap, setSegmentsMap] = useState<Record<number, string>>({})

  const fetchWaypoints = async () => {
    setLoading(true)
    try {
      // If a trackId is provided, use server-side filter via list endpoint (faster, consistent)
      if (selectedTrackId) {
        const trackIdNum = Number(selectedTrackId)
        // Signature: (trackId?, segmentId?, limit?, offset?)
        const segId = segmentFilter && segmentFilter !== 'unsegmented' ? Number(segmentFilter) : undefined
        const { data } = await waypointsApi.listWaypointsArtrackWaypointsGet(trackIdNum as any, segId as any, 10000, 0)
        const arr = Array.isArray(data) ? (data as any) : (data as any)?.items || []
        let filtered = processingFilter ? (arr as any[]).filter((w: any) => w.processing_state === processingFilter) : (arr as any[])
        if (segmentFilter === 'unsegmented') {
          filtered = filtered.filter((w: any) => w.segment_id == null)
        }
        const withIds = filtered.map((w: any) => ({ ...w, id: (w as any).id ?? (w as any).waypoint_id }))
        setWaypoints(withIds as any)
        setTotalRows(withIds.length)
        return
      }

      // Default: list all waypoints (paged) and optionally filter by processing state
      // Signature: (trackId?, segmentId?, limit?, offset?)
      const { data } = await waypointsApi.listWaypointsArtrackWaypointsGet(undefined, undefined, pageSize, page * pageSize)
      const arr = Array.isArray(data) ? (data as any) : (data as any)?.items || []
      const filtered = processingFilter ? (arr as any[]).filter((w: any) => w.processing_state === processingFilter) : (arr as any[])
      const withIds = filtered.map((w) => ({ ...w, id: (w as any).id ?? (w as any).waypoint_id }))
      setWaypoints(withIds as any)
      setTotalRows(Array.isArray(data) ? withIds.length : ((data as any)?.total || withIds.length))
    } catch (error) {
      console.error('Error fetching waypoints:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchModerationQueue = async () => {
    try {
      const { data } = await adminApi.adminModerationQueueArtrackAdminWaypointsModerationGet('pending', 50, 0)
      const items = (data as any)?.items || []
      const withIds = (items as any[]).map((w) => ({ ...w, id: (w as any).id ?? (w as any).waypoint_id }))
      setModerationQueue(withIds as any)
    } catch (error) {
      console.error('Error fetching moderation queue:', error)
    }
  }

  useEffect(() => {
    if (tabValue === 0) {
      fetchWaypoints()
    } else {
      fetchModerationQueue()
    }
  }, [tabValue, page, pageSize, processingFilter, segmentFilter])

  // Fetch segments list for selected track to show names and populate filter
  useEffect(() => {
    const run = async () => {
      if (!selectedTrackId) { setSegmentsMap({}); return }
      try {
        const trackIdNum = Number(selectedTrackId)
        const res = await segmentsApi.listSegmentsArtrackTracksTrackIdSegmentsGet(trackIdNum as any)
        const map: Record<number, string> = {}
        if (Array.isArray(res.data)) {
          for (const s of res.data as any[]) {
            map[s.id] = s.name || `Segment ${s.id}`
          }
        }
        setSegmentsMap(map)
      } catch {
        setSegmentsMap({})
      }
    }
    run()
  }, [selectedTrackId])

  const handleModerate = async (id: number, action: 'approve' | 'reject') => {
    try {
      await adminApi.adminModerateWaypointArtrackAdminWaypointsWaypointIdModeratePost(id, action)
      fetchModerationQueue()
    } catch (error) {
      console.error('Error moderating waypoint:', error)
    }
  }

  const getProcessingStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success'
      case 'processing': return 'warning'
      case 'failed': return 'error'
      default: return 'default'
    }
  }

  const getModerationStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success'
      case 'rejected': return 'error'
      default: return 'warning'
    }
  }

  const waypointColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'track_name',
      headerName: 'Track',
      minWidth: 320,
      flex: 2,
      valueGetter: (params) => params.row?.track_name ?? 'N/A',
    },
    {
      field: 'waypoint_type',
      headerName: 'Type',
      width: 130,
      valueGetter: (p) => p.row?.waypoint_type || (Array.isArray(p.row?.media) && p.row.media.length > 0 ? 'media' : 'gps_track'),
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === 'gps_track' ? 'default' : 'primary'}
        />
      ),
    },
    {
      field: 'segment_id',
      headerName: 'Segment',
      width: 140,
      valueGetter: (p) => {
        const id = p.row?.segment_id
        if (id == null) return 'Unsegmented'
        return segmentsMap[id] || `Segment ${id}`
      },
    },
    {
      field: 'media_type',
      headerName: 'Media Type',
      width: 140,
      valueGetter: (p) => {
        const m = (p.row?.media && Array.isArray(p.row.media) && p.row.media.length) ? p.row.media[0] : null
        return m?.type || '—'
      },
      renderCell: (params) => {
        const v = params.value as string
        const color = v === 'photo' ? 'success' : v === 'video' ? 'warning' : v === 'audio' ? 'info' : 'default'
        return <Chip size="small" label={v} color={color as any} />
      },
    },
    {
      field: 'creator',
      headerName: 'Creator',
      minWidth: 220,
      flex: 1,
      valueGetter: (params) => {
        const c = params.row?.creator
        if (c && typeof c === 'object') {
          return (c.display_name || c.displayName || c.email || c.id || 'N/A')
        }
        // Some responses may include only creator_id
        return params.row?.creator_id ?? 'N/A'
      },
    },
    {
      field: 'location',
      headerName: 'Location',
      minWidth: 320,
      flex: 1.6,
      valueGetter: (params) => {
        const loc = params.row?.location
        return loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number'
          ? `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`
          : 'N/A'
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      minWidth: 180,
      flex: 0.7,
      valueFormatter: (params) => params.value ? new Date(params.value as any).toLocaleDateString() : 'N/A',
    },
    {
      field: 'thumbnail',
      headerName: 'Preview',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const m = (params.row?.media && Array.isArray(params.row.media) && params.row.media.length) ? params.row.media[0] : null
        const thumb = m?.thumbnail_url || m?.url || null
        if (!thumb) return <span style={{ color: '#888' }}>—</span>
        return (
          <a href={m.url} target="_blank" rel="noreferrer" style={{ display: 'inline-block' }}>
            <img src={thumb} alt="thumb" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4 }} />
          </a>
        )
      },
    },
    {
      field: 'processing_state',
      headerName: 'Processing',
      minWidth: 160,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getProcessingStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'moderation_status',
      headerName: 'Moderation',
      minWidth: 140,
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getModerationStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: () => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => { /* Navigate to waypoint details */ }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Moderate">
            <IconButton size="small" onClick={() => { /* Open moderation dialog */ }}>
              <GavelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => { /* Delete waypoint */ }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  const moderationColumns: GridColDef[] = [
    ...waypointColumns.filter(col => col.field !== 'actions'),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => { /* Navigate to waypoint details */ }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            color="success"
            onClick={() => handleModerate(params.row.id, 'approve')}
          >
            Approve
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => handleModerate(params.row.id, 'reject')}
          >
            Reject
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Waypoint Management
      </Typography>

      <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label={`All Waypoints (${totalRows})`} />
        <Tab label={`Moderation Queue (${moderationQueue.length})`} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {/* Filters for All Waypoints */}
        <Paper sx={{ p: 2, mb: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search waypoints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Processing State</InputLabel>
              <Select
                value={processingFilter}
                onChange={(e) => setProcessingFilter(e.target.value)}
                label="Processing State"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            {selectedTrackId && (
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Segment</InputLabel>
                <Select
                  value={segmentFilter}
                  onChange={(e) => setSegmentFilter(e.target.value)}
                  label="Segment"
                >
                  <MenuItem value="">All</MenuItem>
                  {/* We don’t have a list here; users can paste a segment id or we could fetch segments into a separate state. */}
                  {/* For now, allow manual entry via text field if needed, or we’ll enhance by fetching segments list. */}
                </Select>
              </FormControl>
            )}
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchWaypoints}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ height: 'calc(100vh - 220px)', width: '100%', maxWidth: '100%' }}>
          <DataGrid
            rows={waypoints}
            columns={waypointColumns}
            pageSize={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            rowCount={totalRows}
            loading={loading}
            paginationMode="server"
            disableSelectionOnClick
            autoHeight={false}
            getRowId={(row) => (row as any).id ?? (row as any).waypoint_id}
            sx={{ '& .MuiDataGrid-main': { width: '100%' } }}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ height: 'calc(100vh - 220px)', width: '100%', maxWidth: '100%' }}>
          <DataGrid
            rows={moderationQueue}
            columns={moderationColumns}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight={false}
            getRowId={(row) => (row as any).id ?? (row as any).waypoint_id}
            sx={{ '& .MuiDataGrid-main': { width: '100%' } }}
          />
        </Paper>
      </TabPanel>
    </Box>
  )
}